import { useState } from "react";
import { ROLES, OPPORTUNITY_NAME_MAX } from "@salesboard/contracts";
import { Field, Input, Select } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/DatePicker";
import { FormDialog } from "@/components/ui/FormDialog";
import { useT } from "@/i18n";
import { ApiClientError } from "@/lib/api";
import { useSession } from "@/features/auth/useSession";
import { useSelectableClients } from "@/features/clients/hooks";
import { useCreateOpportunity, useOwners } from "./hooks";

/**
 * Creating an opportunity. **Creation only** — editing happens in place on the detail.
 *
 * The four values a user supplies; everything else — stage, status, quarter, year, the
 * timestamps — is set by the system or by a specific operation. Creation is the one moment
 * where several values genuinely arrive together, which is why it keeps a form while every
 * later correction is a single field clicked and committed on its own.
 *
 * The edit mode this dialog used to carry was retired on 2026-08-20 with the hero header;
 * see specs/opportunity-detail-hero.md.
 */
export function OpportunityFormDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useT();
  const session = useSession();
  const isHeadOfSales = session.data?.role === ROLES.headOfSales;

  const [clientId, setClientId] = useState("");
  const [name, setName] = useState("");
  const [nip, setNip] = useState("");
  const [expectedCloseDate, setExpectedCloseDate] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();

  const clients = useSelectableClients("");
  const owners = useOwners();
  const create = useCreateOpportunity();

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFieldErrors({});
    setFormError(undefined);

    try {
      await create.mutateAsync({
        clientId,
        name,
        nip,
        expectedCloseDate,
        ...(isHeadOfSales && ownerId ? { ownerId } : {}),
      });
      onOpenChange(false);
    } catch (error) {
      if (error instanceof ApiClientError && error.isValidation) {
        const flat: Record<string, string> = {};
        for (const [field, messages] of Object.entries(error.fieldErrors)) {
          if (messages[0]) flat[field] = messages[0];
        }
        setFieldErrors(flat);
        return;
      }
      setFormError(error instanceof ApiClientError ? error.message : t("toast.genericFailure"));
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("opportunityForm.title")}
      submitLabel={t("opportunityForm.submit")}
      pending={create.isPending}
      error={formError}
      onSubmit={submit}
    >
      <Field label={t("opportunityForm.client")} htmlFor="opp-client" error={fieldErrors.clientId}>
        <Select id="opp-client" value={clientId} onChange={(event) => setClientId(event.target.value)}>
          <option value="">{t("opportunityForm.chooseClient")}</option>
          {clients.data?.map((client) => (
            <option key={client.id} value={client.id}>
              {client.name}
            </option>
          ))}
        </Select>
      </Field>

      {/* No counter under the field: `maxLength` already stops the typing, and a running
          count is a number nobody is watching for. */}
      <Field label={t("opportunityForm.name")} htmlFor="opp-name" error={fieldErrors.name}>
        <Input
          id="opp-name"
          value={name}
          maxLength={OPPORTUNITY_NAME_MAX}
          onChange={(event) => setName(event.target.value)}
        />
      </Field>

      <Field label={t("opportunityForm.revenue")} htmlFor="opp-nip" error={fieldErrors.nip}>
        <Input
          id="opp-nip"
          value={nip}
          inputMode="decimal"
          placeholder="0.00"
          onChange={(event) => setNip(event.target.value)}
        />
      </Field>

      {/* The derivation is not explained here any more: the detail shows the quarter it
          produced, in brackets beside the date, which teaches the rule where it applies. */}
      <Field label={t("opportunityForm.date")} htmlFor="opp-date" error={fieldErrors.expectedCloseDate}>
        <DatePicker
          id="opp-date"
          label={t("opportunityForm.date")}
          value={expectedCloseDate}
          onChange={setExpectedCloseDate}
        />
      </Field>

      {/* Only the Head of Sales may name a different owner; a Sales Manager always owns
          what they create. Absent rather than disabled, per the book's rule. */}
      {isHeadOfSales && (
        <Field label={t("opportunityForm.owner")} htmlFor="opp-owner">
          <Select id="opp-owner" value={ownerId} onChange={(event) => setOwnerId(event.target.value)}>
            <option value="">{t("opportunityForm.ownerSelf")}</option>
            {owners.data?.map((owner) => (
              <option key={owner.id} value={owner.id}>
                {owner.name}
              </option>
            ))}
          </Select>
        </Field>
      )}
    </FormDialog>
  );
}
